import React, { MouseEvent, useCallback, useRef, useState } from 'react';
import { Picture, PictureTransformations } from 'common/components';
import { imageWithZoom, imageWithZoomContainer } from './image-zoom-styles';

type ImageZoomProps = {
  children: React.ReactNode;
  imgAlt: string;
  imgSrc: string;
  imgTransformations: PictureTransformations;
};

export const ImageZoom = ({ children, imgAlt, imgSrc, imgTransformations }: ImageZoomProps) => {
  const [imageStyle, setImageStyle] = useState({
    opacity: 0,
    top: 0,
    left: 0,
  });

  const containerRef = useRef<HTMLDivElement | null>(null);
  const targetRef = useRef<HTMLImageElement | null>(null);

  const handleMouseMove = useCallback(
    (e: MouseEvent<HTMLImageElement>) => {
      if (!targetRef?.current || !containerRef?.current) {
        return;
      }

      const targetRect = targetRef.current.getBoundingClientRect();
      const containerRect = containerRef.current.getBoundingClientRect();

      const xRatio = (targetRect.width - containerRect.width) / containerRect.width;
      const yRatio = (targetRect.height - containerRect.height) / containerRect.height;

      const left = Math.max(Math.min(e.pageX - containerRect.left, containerRect.width), 0);
      const top = Math.max(Math.min(e.pageY - containerRect.top, containerRect.height), 0);

      setImageStyle({
        opacity: 1,
        top: top * -yRatio,
        left: left * -xRatio,
      });
    },
    [containerRef, targetRef]
  );

  const handleMouseLeave = useCallback(() => {
    setImageStyle({ opacity: 0, top: 0, left: 0 });
  }, []);

  return (
    <div
      className={imageWithZoomContainer}
      onMouseLeave={handleMouseLeave}
      onMouseMove={handleMouseMove}
      ref={containerRef}>
      <Picture
        alt={imgAlt}
        className={imageWithZoom}
        refCb={(el: HTMLImageElement | null) => {
          targetRef.current = el;
        }}
        src={imgSrc}
        style={imageStyle}
        title={imgAlt}
        transformations={imgTransformations}
      />
      {children}
    </div>
  );
};
